import { gql } from '@apollo/client'

// ─── Fragments ─────────────────────────────────────────────────────────────────────────────

const PROVIDER_FIELDS = gql`
  fragment ProviderFields on Provider {
    _id
    provider_name
    specialty
    practice_name
    city
    state
    npi
    email
    email_last_checked
    status
    prescribing_volume_annual_usd
    estimated_total_medicare_usd
    dme_product_mix {
      product
      volume_usd
    }
    medicare_volume {
      total_usd
      dme_usd
      drug_usd
    }
    dme_hcpcs {
      code
      description
      volume_usd
      claims
      rental
    }
    cms_rank_by_volume
    data_year
    cms_last_updated
    cms_no_data
    engagement_signals {
      date
      signal_type
      detail
    }
    outreach_email {
      subject
      body
      sent_at
    }
    phone
    fax
    address
    zip
    years_in_practice
    ehr_system
    multi_location
    entity_type
    medicare_type
    ruca_category
    total_benes
    bene_avg_age
    has_rental_items
    website_url
    selectedForCampaign
  }
`

// ─── Provider queries ─────────────────────────────────────────────────────────────────────────

export const GET_PROVIDERS = gql`
  ${PROVIDER_FIELDS}
  query GetProviders($clientId: ID!) {
    providers(clientId: $clientId) {
      ...ProviderFields
    }
  }
`

export const GET_ENGAGEMENT = gql`
  ${PROVIDER_FIELDS}
  query GetEngagement($clientId: ID!) {
    providerEngagement(clientId: $clientId) {
      ...ProviderFields
    }
  }
`

export const GET_OUTREACH_RUN = gql`
  query GetOutreachRun($clientId: ID!, $runId: ID!) {
    outreachRun(clientId: $clientId, runId: $runId) {
      runId
      status
      email {
        subject
        body
      }
      error
      nodeRuntimeData
      completedNodes
      progressPct
      graphNodes
      provenance {
        sentence
        claims {
          text
          source
          field
          actualValue
          verified
        }
      }
    }
  }
`

// ─── Provider mutations ──────────────────────────────────────────────────────────────────────────

export const ADD_PROVIDERS_TO_PIPELINE = gql`
  mutation AddProvidersToPipeline($clientId: ID!, $providers: [ProviderInput!]!) {
    addProvidersToPipeline(clientId: $clientId, providers: $providers) {
      added
    }
  }
`

export const UPDATE_PROVIDER_STATUS = gql`
  ${PROVIDER_FIELDS}
  mutation UpdateProviderStatus(
    $clientId: ID!, $providerId: ID!,
    $status: ProviderStatus!,
    $engagementSignal: EngagementSignalInput,
    $outreachEmail: OutreachEmailInput
  ) {
    updateProviderStatus(
      clientId: $clientId, providerId: $providerId,
      status: $status,
      engagementSignal: $engagementSignal,
      outreachEmail: $outreachEmail
    ) {
      ...ProviderFields
    }
  }
`

export const REMOVE_PROVIDER = gql`
  mutation RemoveProvider($clientId: ID!, $providerId: ID!) {
    removeProvider(clientId: $clientId, providerId: $providerId)
  }
`

export const ENRICH_PROVIDER_CMS = gql`
  ${PROVIDER_FIELDS}
  mutation EnrichProviderCms($clientId: ID!, $providerId: ID!) {
    enrichProviderCms(clientId: $clientId, providerId: $providerId) {
      ...ProviderFields
    }
  }
`

export const ENRICH_PROVIDER_EMAIL = gql`
  ${PROVIDER_FIELDS}
  mutation EnrichProviderEmail($clientId: ID!, $providerId: ID!, $useUserGemini: Boolean) {
    enrichProviderEmail(clientId: $clientId, providerId: $providerId, useUserGemini: $useUserGemini) {
      ...ProviderFields
    }
  }
`

export const PREFETCH_PROVIDER_CMS = gql`
  mutation PrefetchProviderCms($npi: String!) {
    prefetchProviderCms(npi: $npi) {
      npi
      prescribing_volume_annual_usd
      estimated_total_medicare_usd
      dme_product_mix { product volume_usd }
      medicare_volume { total_usd dme_usd drug_usd }
      dme_hcpcs { code description volume_usd claims rental }
      cms_rank_by_volume
      data_year
      cms_no_data
    }
  }
`

export const GENERATE_OUTREACH_EMAIL = gql`
  mutation GenerateOutreachEmail($clientId: ID!, $providerId: ID!) {
    generateOutreachEmail(clientId: $clientId, providerId: $providerId) {
      runId
    }
  }
`

export const CHECK_OUTREACH_COMPLIANCE = gql`
  mutation CheckOutreachCompliance(
    $clientId: ID!, $providerId: ID!,
    $subject: String!, $body: String!
  ) {
    checkOutreachCompliance(
      clientId: $clientId, providerId: $providerId,
      subject: $subject, body: $body
    ) {
      overall_severity
      checks {
        label
        severity
        detail
      }
    }
  }
`

export const PUBLISH_OUTREACH_EMAIL = gql`
  mutation PublishOutreachEmail(
    $clientId: ID!
    $providerId: ID!
    $subject: String!
    $body: String!
    $targetEmail: String
  ) {
    publishOutreachEmail(
      clientId: $clientId
      providerId: $providerId
      subject: $subject
      body: $body
      targetEmail: $targetEmail
    ) {
      id
      status
      createdAt
    }
  }
`

export const SEARCH_PROVIDERS_BY_NAME = gql`
  query SearchProvidersByName($clientId: String!, $name: String!, $enumerationType: String, $limit: Int) {
    searchProvidersByName(clientId: $clientId, name: $name, enumerationType: $enumerationType, limit: $limit) {
      npi
      provider_name
      specialty
      practice_name
      city
      state
      address
      zip
      already_in_pipeline
    }
  }
`

export const UPDATE_PROVIDER_EMAIL = gql`
  mutation UpdateProviderEmail($clientId: String!, $providerId: String!, $email: String!) {
    updateProviderEmail(clientId: $clientId, providerId: $providerId, email: $email)
  }
`

export const UPDATE_PROVIDER = gql`
  ${PROVIDER_FIELDS}
  mutation UpdateProvider($clientId: ID!, $providerId: ID!, $input: UpdateProviderInput!) {
    updateProvider(clientId: $clientId, providerId: $providerId, input: $input) {
      ...ProviderFields
    }
  }
`

export const SET_PROVIDERS_CAMPAIGN_SELECTION = gql`
  mutation SetProvidersCampaignSelection($clientId: ID!, $providerIds: [ID!]!, $selected: Boolean!) {
    setProvidersCampaignSelection(clientId: $clientId, providerIds: $providerIds, selected: $selected) {
      _id
      selectedForCampaign
    }
  }
`

// ─── Outreach queue operations ──────────────────────────────────────────────────────────────────────────

const OUTREACH_QUEUE_ITEM_FIELDS = gql`
  fragment OutreachQueueItemFields on OutreachQueueItem {
    id
    clientId
    providerNpi
    providerName
    providerPractice
    subject
    body
    qualityScore
    icpScore
    icpTier
    status
    sequenceId
    sequencePosition
    nextFollowupAt
    psychologyStrategy
    discoverySource
    createdAt
    sentAt
    approvedAt
  }
`

export const GET_OUTREACH_QUEUE = gql`
  ${OUTREACH_QUEUE_ITEM_FIELDS}
  query GetOutreachQueue($clientId: ID!, $status: String) {
    outreachQueue(clientId: $clientId, status: $status) {
      ...OutreachQueueItemFields
    }
  }
`

export const APPROVE_OUTREACH_ITEM = gql`
  ${OUTREACH_QUEUE_ITEM_FIELDS}
  mutation ApproveOutreachItem($clientId: ID!, $itemId: ID!) {
    approveOutreachItem(clientId: $clientId, itemId: $itemId) {
      ...OutreachQueueItemFields
    }
  }
`

export const REJECT_OUTREACH_ITEM = gql`
  ${OUTREACH_QUEUE_ITEM_FIELDS}
  mutation RejectOutreachItem($clientId: ID!, $itemId: ID!, $reason: String) {
    rejectOutreachItem(clientId: $clientId, itemId: $itemId, reason: $reason) {
      ...OutreachQueueItemFields
    }
  }
`
